import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { RequestService } from './request.service';
import {
  EstadoPlataformaDTO,
  MantenimientoPlataformaPublico,
  PaginaHistorialPublicacionesDTO,
  PendientesPublicacionesDTO,
  PublicacionErpDTO,
  PublicacionVistaDTO,
} from './workspace/DTO/publicacion-plataforma.dto';
import { NotificacionesWorkspaceService } from './workspace/notificaciones-workspace.service';
import { UsuariosActividadTrackerService } from './workspace/usuarios-actividad-tracker.service';

@Injectable({ providedIn: 'root' })
export class PublicacionesPlataformaService {
  private activoSubject = new BehaviorSubject<MantenimientoPlataformaPublico | null>(null);
  private estadoSubject = new BehaviorSubject<EstadoPlataformaDTO | null>(null);
  private pendientesSubject = new BehaviorSubject<PendientesPublicacionesDTO | null>(null);
  private avisosCerradosSubject = new BehaviorSubject<Set<string>>(new Set());
  private avisosCerradosSesion = new Set<string>();
  private tokenSesion = '';
  private consultaSubscription: Subscription;
  private temporizadorInicio: any;
  private temporizadorFin: any;
  private desfaseServidorMs = 0;

  activo$ = this.activoSubject.asObservable();
  estado$ = this.estadoSubject.asObservable();
  pendientes$ = this.pendientesSubject.asObservable();
  avisosCerrados$ = this.avisosCerradosSubject.asObservable();

  /**
   * Recibe HTTP, el tracker de actividad, la sesion vigente y el socket ya
   * autenticado por la bandeja de notificaciones.
   */
  constructor(
    private http: HttpClient,
    private actividadTracker: UsuariosActividadTrackerService,
    private requestServ: RequestService,
    private notificacionesServ: NotificacionesWorkspaceService
  ) {}

  /**
   * Arranca un solo ciclo de consultas: el polling de 60 s mientras hay
   * actividad, y una consulta inmediata con cada aviso de socket o
   * reconexion. La API siempre es la fuente de verdad.
   */
  iniciar(): void {
    if (this.consultaSubscription) return;
    this.consultaSubscription = merge(
      this.actividadTracker.crearPolling(60000, 'Publicaciones de plataforma'),
      this.notificacionesServ.publicaciones$,
      this.notificacionesServ.reconexion$
    )
      .pipe(switchMap(() => this.consultarTodo()))
      .subscribe();
  }

  /** Headers silenciosos: sin spinner global ni alerta por un fallo del sondeo. */
  private headersSilenciosos(): HttpHeaders {
    let headers = new HttpHeaders({
      'X-Skip-Loading': 'true',
      'X-Skip-Global-Error': 'true',
    });
    const token = this.requestServ.master?.apiKey;
    if (token) headers = headers.set('token', token);
    return headers;
  }

  /**
   * Consulta el estado publico (mantenimiento activo, los de hoy y el bypass)
   * sin token obligatorio; un fallo se ignora porque el middleware 503 sigue
   * protegiendo las operaciones de negocio.
   */
  consultarEstado(): Observable<EstadoPlataformaDTO | null> {
    return this.http
      .get<any>(`${environment.apiUrl}/publicaciones/estado`, { headers: this.headersSilenciosos() })
      .pipe(
        map(response => response.respuesta as EstadoPlataformaDTO),
        catchError(() => of(null))
      );
  }

  /**
   * Novedades pendientes del usuario y avisos de banner que ya cerro; sin
   * sesion no hay usuario y se responde vacio sin llamar al API.
   */
  consultarPendientes(): Observable<PendientesPublicacionesDTO | null> {
    if (!this.requestServ.master?.apiKey) return of(null);
    return this.http
      .get<any>(`${environment.apiUrl}/clientes/publicaciones/pendientes`, { headers: this.headersSilenciosos() })
      .pipe(
        map(response => response.respuesta as PendientesPublicacionesDTO),
        catchError(() => of(null))
      );
  }

  /**
   * Consulta estado y pendientes en paralelo y adopta cada respuesta valida;
   * un fallo de una no descarta la otra.
   */
  private consultarTodo(): Observable<void> {
    return forkJoin([this.consultarEstado(), this.consultarPendientes()]).pipe(
      map(([estado, pendientes]) => {
        if (estado) this.aplicarEstado(estado);
        this.aplicarPendientes(pendientes);
      })
    );
  }

  /**
   * Consulta inmediata pedida por el login o una pantalla, sin reiniciar el
   * polling existente.
   */
  refrescar(): Promise<void> {
    return this.consultarTodo().toPromise();
  }

  /**
   * Adopta los pendientes del servidor. Al cambiar el token se olvidan los
   * avisos cerrados en la sesion anterior; sin sesion no hay pendientes.
   */
  private aplicarPendientes(pendientes: PendientesPublicacionesDTO | null): void {
    const token = this.requestServ.master?.apiKey || '';
    if (token !== this.tokenSesion) {
      this.tokenSesion = token;
      this.avisosCerradosSesion.clear();
      this.pendientesSubject.next(null);
    }
    if (!token) {
      this.publicarAvisosCerrados();
      return;
    }
    if (pendientes) this.pendientesSubject.next(pendientes);
    this.publicarAvisosCerrados();
  }

  /** Une los avisos cerrados guardados en el servidor con los de esta sesion. */
  private publicarAvisosCerrados(): void {
    const cerrados = new Set(this.avisosCerradosSesion);
    (this.pendientesSubject.value?.avisosVistos || []).forEach(aviso =>
      cerrados.add(this.claveAviso(aviso.uuid, aviso.revision))
    );
    this.avisosCerradosSubject.next(cerrados);
  }

  /** Identidad de un aviso por uuid y revision: una reprogramacion lo vuelve a mostrar. */
  claveAviso(uuid: string, revision: number): string {
    return `${uuid}:${revision}`;
  }

  /**
   * Registra en el servidor las revisiones vistas y las retira de inmediato
   * de los pendientes locales para que el popup no reaparezca mientras se
   * confirma; luego vuelve a consultar. Un fallo deja los pendientes como
   * estaban y se reintentara la proxima vez.
   */
  async marcarVistas(items: PublicacionVistaDTO[]): Promise<boolean> {
    if (!items.length || !this.requestServ.master?.apiKey) return false;
    try {
      await this.http
        .post<any>(`${environment.apiUrl}/clientes/publicaciones/visto`, { items }, { headers: this.headersSilenciosos() })
        .toPromise();
    } catch (error) {
      return false;
    }
    const actual = this.pendientesSubject.value;
    if (actual) {
      const vistas = new Map(items.map(item => [item.uuid, item.revision]));
      const restantes = actual.items.filter(p => !(vistas.get(p.uuid) >= p.revision));
      this.pendientesSubject.next({
        ...actual,
        items: restantes,
        total: Math.max(0, actual.total - (actual.items.length - restantes.length)),
      });
    }
    this.consultarPendientes().subscribe(pendientes => this.aplicarPendientes(pendientes));
    return true;
  }

  /**
   * Cierra un aviso de banner: se oculta al instante en esta sesion y, con
   * usuario autenticado, se guarda en el servidor para sus otros navegadores.
   */
  cerrarAviso(mantenimiento: MantenimientoPlataformaPublico): void {
    this.avisosCerradosSesion.add(this.claveAviso(mantenimiento.uuid, mantenimiento.revisionAviso));
    this.publicarAvisosCerrados();
    this.marcarVistas([{ uuid: mantenimiento.uuid, revision: mantenimiento.revisionAviso }]);
  }

  /** Historial paginado de publicaciones visibles con la marca de vista. */
  listarHistorial(page: number, limit: number): Promise<PaginaHistorialPublicacionesDTO> {
    const params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    return this.http
      .get<any>(`${environment.apiUrl}/clientes/publicaciones`, { headers: this.headersSilenciosos(), params })
      .pipe(map(response => response.respuesta as PaginaHistorialPublicacionesDTO))
      .toPromise();
  }

  /** Detalle de una publicacion del historial con sus paginas. */
  obtenerDetalle(uuid: string): Promise<PublicacionErpDTO> {
    return this.http
      .get<any>(`${environment.apiUrl}/clientes/publicaciones/${encodeURIComponent(uuid)}`, { headers: this.headersSilenciosos() })
      .pipe(map(response => response.respuesta as PublicacionErpDTO))
      .toPromise();
  }

  /**
   * Provee una hora aproximada del servidor usando el desfase observado en la
   * ultima respuesta, evitando que el contador dependa del reloj local.
   */
  obtenerAhoraServidor(): number {
    return Date.now() + this.desfaseServidorMs;
  }

  /**
   * Adopta la hora y agenda devueltas por el servidor y publica los avisos del
   * dia. Al llegar el inicio de la proxima ventana vuelve a consultar: el
   * servidor decide si bloquea, porque las ventanas de hoy tambien incluyen
   * las que no bloquean.
   */
  private aplicarEstado(estado: EstadoPlataformaDTO): void {
    this.desfaseServidorMs = new Date(estado.fechaServidor).getTime() - Date.now();
    this.estadoSubject.next(estado);
    const mantenimientoBloqueante = estado.accesoPermitidoDuranteMantenimiento
      ? null
      : estado.mantenimientoActivo;
    this.activoSubject.next(mantenimientoBloqueante || null);
    this.limpiarTemporizadores();
    const ahoraServidor = new Date(estado.fechaServidor).getTime();
    const proximo = (estado.mantenimientosDeHoy || []).find(item =>
      new Date(item.fechaInicio).getTime() > ahoraServidor
    );
    if (mantenimientoBloqueante) {
      this.programarFin(mantenimientoBloqueante, ahoraServidor);
    } else if (proximo) {
      const espera = new Date(proximo.fechaInicio).getTime() - ahoraServidor;
      this.temporizadorInicio = setTimeout(() => {
        this.consultarTodo().subscribe();
      }, Math.max(0, espera));
    }
  }

  /**
   * Activa de inmediato la capa bloqueante usando la respuesta 503 autoritativa
   * recibida por cualquier operacion V1 o V2.
   */
  activarDesdeError(mantenimiento: MantenimientoPlataformaPublico): void {
    if (!mantenimiento || mantenimiento.uuid === this.activoSubject.value?.uuid) return;
    this.activoSubject.next(mantenimiento);
    this.programarFin(mantenimiento, Date.now());
  }

  /**
   * Retira el bloqueo visual al finalizar la estimacion y vuelve a consultar al
   * servidor por si la agenda fue modificada durante la ventana.
   */
  private programarFin(
    mantenimiento: MantenimientoPlataformaPublico,
    ahoraReferencia: number
  ): void {
    clearTimeout(this.temporizadorFin);
    const espera = new Date(mantenimiento.fechaFin).getTime() - ahoraReferencia;
    this.temporizadorFin = setTimeout(() => {
      this.activoSubject.next(null);
      this.consultarTodo().subscribe();
    }, Math.max(0, espera));
  }

  /**
   * Cancela callbacks reemplazados por un estado central mas reciente para que
   * una reprogramacion no active ni cierre ventanas obsoletas.
   */
  private limpiarTemporizadores(): void {
    clearTimeout(this.temporizadorInicio);
    clearTimeout(this.temporizadorFin);
  }
}
